import classNames from 'classnames';
import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import FilterChip from '../FilterChip/FilterChip';
import SearchInput from '../SearchInput';
import FilterDropdown from './FilterDropdown';
import styles from './styles/styles.module.scss';
import { CustomFilterChip, FilterConfig } from './types';

type FilterBarProps = {
  className?: string;
  filters: (CustomFilterChip | FilterConfig)[];
  filtersContainerClassName?: string;
  filterChipClassName?: string;
  id?: string;
  openDropdownId?: string; // Used to force-open a dropdown from the consuming component
  popupMenuContainerClassName?: string;
  onFilterClose?: (id: string) => void;
  onFilterOpen?: (id: string) => void;
  searchInput?: Pick<ComponentProps<typeof SearchInput>, 'onChange' | 'placeholder' | 'testId' | 'value'>;
  searchInputClassName?: string;
  truncateFilterChips?: boolean;
};

export default function FilterBar({
  className,
  filters,
  filtersContainerClassName,
  filterChipClassName,
  id,
  popupMenuContainerClassName,
  onFilterClose,
  onFilterOpen,
  openDropdownId,
  searchInput,
  searchInputClassName,
  truncateFilterChips
}: FilterBarProps) {
  const [openId, setOpenId] = useState(openDropdownId);

  const openFilter = useCallback(
    (id: string) => {
      onFilterOpen?.(id);
      setOpenId(id);
    },
    [onFilterOpen]
  );

  const closeFilter = useCallback(
    (id: string) => {
      onFilterClose?.(id);
      setOpenId(undefined);
    },
    [onFilterClose]
  );

  useEffect(() => {
    if (openDropdownId) {
      openFilter(openDropdownId);
    }
    // We only want to trigger this when openDropdownId changes
  }, [openDropdownId]);

  const filterChips = useMemo(() => {
    return filters.map((filter, index) => {
      const key = `filter-${index}`;
      if (filter.type === 'custom-chip') {
        return <FilterChip className={filterChipClassName} {...filter.filterChip} key={key} size="sm" truncate={truncateFilterChips} />;
      }
      const isOpen = openId === filter.id;
      return (
        <FilterDropdown
          key={key}
          popupMenuContainerClassName={popupMenuContainerClassName}
          anchor={
            <FilterChip
              className={filterChipClassName}
              {...filter.filterChip}
              isOpen={isOpen}
              onClick={e => {
                filter.filterChip.onClick?.(e);
                if (isOpen) {
                  closeFilter(filter.id);
                } else {
                  openFilter(filter.id);
                }
              }}
              onClear={e => {
                filter.filterChip.onClear?.(e);
                closeFilter(filter.id);
              }}
              size="sm"
              truncate={truncateFilterChips}
            />
          }
          config={filter}
          isOpen={isOpen}
          onCloseRequest={() => {
            // Prevents open/close flash when one dropdown is closed by clicking on another dropdown
            setOpenId(currentId => {
              if (currentId === openId) {
                onFilterClose?.(filter.id);
                return undefined;
              }
              return currentId;
            });
          }}
        />
      );
    });
  }, [filters, openId, popupMenuContainerClassName, filterChipClassName, truncateFilterChips, closeFilter, openFilter, onFilterClose]);

  return (
    <div id={id} className={classNames(styles.filterBar, className)}>
      {searchInput ? (
        <div className={classNames(styles.searchInputContainer, searchInputClassName)}>
          <SearchInput {...searchInput} className={styles.searchInput} size="sm" />
        </div>
      ) : null}
      <div className={classNames(styles.filters, filtersContainerClassName)}>{filterChips}</div>
    </div>
  );
}
